import React, { useEffect, useState } from "react";
import Layout from "./Layout";
import { useNavigate } from "react-router-dom";
import "../css/RewardUserList.css"; // 스타일 시트 유지
import { defaultAxiosInstance } from "../api/defaultInstance";

function RewardUserList() {
  const navigate = useNavigate();

  // --- 날짜 및 형식 관련 함수 (변경 없음) ---
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // --- 상태 변수 (변경 없음) ---
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0); // AdManagement의 summary.totalCount 대신 사용
  const [searchParams, setSearchParams] = useState({ // AdManagement의 filter 대신 사용
    startDate: formatDate(firstDay),
    endDate: formatDate(today),
    email: "",
    name: "",
    referralCode: "",
    page: 1,
    limit: 20, // 한 페이지당 항목 수
  });
  const [tempSearchParams, setTempSearchParams] = useState({ // 임시 검색 파라미터 (입력 중인 값들)
    startDate: formatDate(firstDay),
    endDate: formatDate(today),
    email: "",
    name: "",
    referralCode: "",
  });
  const [summary, setSummary] = useState({
    invitationCount: 0,
    conversionCount: 0,
    maintainedCount: 0,
    canceledCount: 0,
  });

  // --- 데이터 로딩 함수 (변경 없음) ---
  const fetchSummary = async (currentParams = searchParams) => { // 파라미터 받을 수 있게 수정 (선택적)
    try {
      // 빈 값인 필드를 제외하는 함수
      const filterEmptyValues = (obj) => {
        const filtered = {};
        Object.keys(obj).forEach(key => {
          if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
            filtered[key] = obj[key];
          }
        });
        return filtered;
      };

      const summaryParams = filterEmptyValues({
        startDate: currentParams.startDate,
        endDate: currentParams.endDate,
        email: currentParams.email,
        name: currentParams.name,
        referralCode: currentParams.referralCode,
      });

      const response = await defaultAxiosInstance.get("/referral/admin/statistics", {
        params: summaryParams
      });
      setSummary(response.data.data);
    } catch (error) {
        console.error("Error fetching summary:", error);
        // 에러 처리 (예: 사용자에게 알림)
    }
  };

  const fetchUsers = async (currentParams = searchParams) => { // 파라미터 받을 수 있게 수정
     try {
        // 빈 값인 필드를 제외하는 함수
        const filterEmptyValues = (obj) => {
          const filtered = {};
          Object.keys(obj).forEach(key => {
            if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
              filtered[key] = obj[key];
            }
          });
          return filtered;
        };

        const filteredParams = filterEmptyValues(currentParams);
        
        const response = await defaultAxiosInstance.get("/referral/admin/members", {
            params: filteredParams, // 빈 값이 제거된 파라미터 전달
        });
        const result = response.data.data;
        setUsers(Array.isArray(result.list) ? result.list : []);
        setTotal(result.total || 0);
     } catch (error) {
        console.error("Error fetching users:", error);
        // 에러 처리
        setUsers([]);
        setTotal(0);
     }
  };

  // --- 초기 데이터 로딩 및 페이지 변경 감지 useEffect ---
  useEffect(() => {
    fetchSummary(); // 초기 요약 정보 로딩
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 마운트 시 1회만 실행

  useEffect(() => {
    fetchUsers(); // searchParams 객체 자체가 변경될 때마다 사용자 목록 다시 로드 (페이지, 필터 변경 시 모두 포함)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // 페이지 뿐만 아니라 필터 변경 시에도 fetchUsers 호출되도록 searchParams 전체를 의존성 배열에 넣음


  // --- 핸들러 함수들 ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTempSearchParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setSearchParams((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleSearch = () => {
    // 임시 검색 파라미터를 실제 검색 파라미터로 복사하고 페이지를 1로 리셋
    const newSearchParams = { ...tempSearchParams, page: 1, limit: searchParams.limit };
    setSearchParams(newSearchParams);
    // 요약 정보와 사용자 목록을 새로운 필터 기준으로 다시 가져옴
    fetchSummary(newSearchParams);
    fetchUsers(newSearchParams);
  };

  const handleReset = () => {
    const initialTempParams = {
      startDate: formatDate(firstDay),
      endDate: formatDate(today),
      email: "",
      name: "",
      referralCode: "",
    };
    setTempSearchParams(initialTempParams);
    const initialSearchParams = { ...initialTempParams, page: 1, limit: searchParams.limit };
    setSearchParams(initialSearchParams);
    fetchSummary(initialSearchParams);
    fetchUsers(initialSearchParams);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // (수정됨) 특정 페이지 번호 클릭 핸들러 추가
  const handlePageClick = (page) => {
    // searchParams의 page만 업데이트 -> useEffect 트리거
    setSearchParams(prev => ({ ...prev, page: page }));
  };

  // (수정됨) 이전 페이지 버튼 핸들러
  const handlePrevPage = () => {
    // handlePageClick 호출
    if (searchParams.page > 1) {
        handlePageClick(searchParams.page - 1);
    }
  };

  // (수정됨) 다음 페이지 버튼 핸들러
  const handleNextPage = () => {
    // handlePageClick 호출 (마지막 페이지인지 확인 후 호출)
    const totalPages = Math.ceil(total / searchParams.limit);
    if (searchParams.page < totalPages) {
        handlePageClick(searchParams.page + 1);
    }
  };

  const handleRowClick = (user) => {
    navigate("/rewards/users/detail", {
      state: {
        referralCodeId: user.referralCodeId,
        email: user.email,
        name: user.name,
        referralCode: user.referralCode,
        // 상세 페이지로 이동 시 현재 필터의 날짜 정보도 전달
        startDate: searchParams.startDate,
        endDate: searchParams.endDate,
      },
    });
  };

  // --- 렌더링 계산 (변경 없음) ---
  const startIndex = total === 0 ? 0 : (searchParams.page - 1) * searchParams.limit + 1;
  const endIndex = Math.min(searchParams.page * searchParams.limit, total);
  const totalPages = Math.ceil(total / searchParams.limit); // 총 페이지 수 계산

  return (
    <Layout>
      <div className="user-list-container">
        {/* 1. 통계 섹션 */}
        <div className="summary">
          <div className="summary-box">누적 초대 수<br /><strong>{summary.invitationCount?.toLocaleString()}</strong></div>
          <div className="summary-box">누적 전환 수<br /><strong>{summary.conversionCount?.toLocaleString()}</strong></div>
          <div className="summary-box">3개월 차 회원 수<br /><strong>{summary.maintainedCount?.toLocaleString()}</strong></div>
          <div className="summary-box">중도 해지 회원 수<br /><strong>{summary.canceledCount?.toLocaleString()}</strong></div>
        </div>

        {/* 2. 필터 섹션 */}
        <div className="filter-bar">
          <div className="filter-group">
            <label>초대코드 입력일 시작</label>
            <input name="startDate" type="date" value={tempSearchParams.startDate} onChange={handleInputChange} onKeyDown={handleKeyPress} />
          </div>
          <div className="filter-group">
            <label>초대코드 입력일 종료</label>
            <input name="endDate" type="date" value={tempSearchParams.endDate} onChange={handleInputChange} onKeyDown={handleKeyPress} />
          </div>
          <div className="filter-group">
            <label>페이지당 항목 수</label>
            <select value={searchParams.limit} onChange={handleLimitChange}>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
          <div className="filter-group"> </div>
          <div className="filter-group">
            <label>아이디</label>
            <input name="email" placeholder="아이디 입력" value={tempSearchParams.email} onChange={handleInputChange} onKeyDown={handleKeyPress} />
          </div>
          <div className="filter-group">
            <label>이름</label>
            <input name="name" placeholder="이름 입력" value={tempSearchParams.name} onChange={handleInputChange} onKeyDown={handleKeyPress}/>
          </div>
          <div className="filter-group">
            <label>코드</label>
            <input name="referralCode" placeholder="코드 입력" value={tempSearchParams.referralCode} onChange={handleInputChange} onKeyDown={handleKeyPress}/>
          </div>
          <div className="filter-group">
            <button onClick={handleSearch}>필터 적용</button>
            <button onClick={handleReset} className="reset-btn">초기화</button>
          </div>
        </div>

        {/* 3. 테이블 헤더 섹션 */}
        <div className="table-header">
          <h3 className="section-title">회원 리스트</h3>
        </div>

        {/* 4. 테이블 섹션 */}
        <div className="user-table">
          <table>
            <thead>
              <tr>
                <th>회원 ID</th>
                <th>이름</th>
                <th>가입일</th>
                <th>추천코드</th>
                <th>올해 누적 PIE</th>
                <th>보유 PIE</th>
                <th>이번 달 지급 예정 PIE</th>
                <th>전환한 금액(원)</th>
                <th>초대 수</th>
                <th>전환 수</th>
                <th>해지 수</th>
                <th>멤버십</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                  <tr><td colSpan="12" style={{ textAlign: 'center' }}>조회된 회원이 없습니다.</td></tr>
              ) : (
                  users.map((user) => (
                  <tr key={user.referralCodeId || user.email} onClick={() => handleRowClick(user)} style={{ cursor: "pointer" }}>
                    <td>{user.email}</td>
                    <td>{user.name}</td>
                    <td>{user.joinedAt ? formatDate(new Date(user.joinedAt)) : '-'}</td>
                    <td>{user.referralCode}</td>
                    <td>{user.totalReward?.toLocaleString()}</td>
                    <td>{user.accumulatedPie?.toLocaleString()}</td>
                    <td>{user.pendingReward?.toLocaleString()}</td>
                    <td>{user.totalCashConverted?.toLocaleString()}</td>
                    <td>{user.invitationCount}</td>
                    <td>{user.conversionCount}</td>
                    <td>{user.cancellationCount}</td>
                    <td>
                      <button className={`status-button ${user.status?.toLowerCase()}`}>{user.status || '정보없음'}</button>
                    </td>
                  </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>

        {/* 5. 페이징 섹션 */}
        <div className="pagination-info">
          <div>총 {total.toLocaleString()}개 항목 중 {startIndex.toLocaleString()} - {endIndex.toLocaleString()}</div>
          <div className="pagination">
            <button onClick={handlePrevPage} disabled={searchParams.page === 1}>이전</button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={searchParams.page === i + 1 ? "active" : ""}
                onClick={() => handlePageClick(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button onClick={handleNextPage} disabled={searchParams.page >= totalPages}>다음</button>
          </div>
        </div>

      </div>
    </Layout>
  );
}

export default RewardUserList;